import { ComponentType, ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { DefaultTheme, ThemeProvider } from 'styled-components';
import { setDefaultLocale, registerLocale } from 'react-datepicker';

// https://github.com/Hacker0x01/react-datepicker/blob/main/README.md#localization
import es from 'date-fns/locale/es';
import de from 'date-fns/locale/de';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import zhHK from 'date-fns/locale/zh-HK';
import ja from 'date-fns/locale/ja';
import hi from 'date-fns/locale/hi';

import { THEMES } from '../core/theme';
import i18nInstance from '../services/i18n';

// Need to register the languages for the date picker to pick up the correct locale
// Update this list as we update language choices at /account/languages
registerLocale('es', es);
registerLocale('de', de);
registerLocale('fr', fr);
registerLocale('it', it);
registerLocale('zh', zhHK);
registerLocale('ja', ja);
registerLocale('hi', hi);

type AppType = 'cobalt' | 'default';

export let CURRENT_EDNA_THEME: DefaultTheme;

// Setting the default language for the datepicker based on user's selection, otherwise it defaults to English
const currentLanguage = i18nInstance.language;
setDefaultLocale(currentLanguage.split('-')[0]);

// Being called from the client when user changes the language
export const setEdnaLanguage = (language: string) => {
  i18nInstance.changeLanguage(language);
  setDefaultLocale(language.split('-')[0]);
};
export const setCurrentTheme = (type: AppType) => {
  CURRENT_EDNA_THEME = type === 'default' ? THEMES.orange : THEMES.cobalt;
};

export const getCurrentTheme = (): DefaultTheme => {
  try {
    if (CURRENT_EDNA_THEME === undefined) {
      throw new Error(
        '[EdnaWrapper] No theme current set on Edna, please call setCurrentTheme to use the library'
      );
    }

    return CURRENT_EDNA_THEME;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    // defaults to orange
    return THEMES.orange;
  }
};

// The Wrapper is meant to be used when you want to wrap your component with the ThemeProvider from styled-components,
// with all the functionality, if needed.
export const EdnaWrapper = (props: { children: ReactNode }) => {
  const { children } = props;
  const theme = getCurrentTheme();

  const WrappedComponent = (
    // For Edna we set up i18n with generating a new instance of it due to the conflict with a client.
    // Need to wrap up Edna components in I18nextProvider, so the translations are available
    <I18nextProvider i18n={i18nInstance} defaultNS={'translation'}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </I18nextProvider>
  );

  return WrappedComponent;
};

// The HOC returns only the theme prop for the rendered component, no theme involved. Can be used more broadly than
// the Wrapper, as it's not on the code itself, just on the export.
export function withEdna(Component: ComponentType<any>) {
  const withEdnaTheme = (props: any) => (
    <Component theme={CURRENT_EDNA_THEME} {...props} />
  );

  return withEdnaTheme;
}
